import { ReactComponent as EmptyPanelDroneIcon } from '@assets/components/panel/notification/empty-panel-drone-icon.svg';
import { IcEdit, IcPlus } from '@assets/icons/24x24';
import { Center, HStack, Icon, Spinner, Text, VStack } from '@chakra-ui/react';
import { emptyDrone } from '@components/cockpit/panels/utm/drone/DroneFormContainer';
import DataSheetCardLink from '@components/common/layout/DataSheetCardLink';
import { useAppSelector } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { hasAnySensorRoleFromActiveProfile, selectActiveGroupOrganization } from '@redux/authent/authent.selectors';
import { updateSelectedDrone } from '@redux/global/global.reducer';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetDroneByCodeQuery } from '@services/utm/drone.api';
import { getUniqueSensorFamilies } from '@utils/target.utils';
import { FormattedMessage, useIntl } from 'react-intl';

import { Target } from '@/types/c2/c2.types';
import { ActionEnum } from '@/types/commons/commons.types';
import { SensorAction, TargetLocation } from '@/types/sensor/sensor.types';
import { PartialDrone } from '@/types/utm/drone.types';

import PopupContainer from '../../../common/PopupContainer';
import InfoValue from '../../InfoValue';
import ClearanceSelect from './ClearanceSelect';

type Props = {
  target: Target<TargetLocation>;
};

export default function TargetDrone({ target }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const organization = useAppSelector(selectActiveGroupOrganization);
  const dispatchUpdateSelectedDrone = useWithDispatch(updateSelectedDrone);
  const { data: drone, isLoading } = useGetDroneByCodeQuery(
    target.droneCode ? { code: target.droneCode, site: target.site } : skipToken,
  );
  const canManageTarget = useAppSelector((state) =>
    hasAnySensorRoleFromActiveProfile(
      state,
      getUniqueSensorFamilies(target.trackIds).map(
        (family) => ({ action: ActionEnum.MANAGE_TARGET, sensorFamily: family }) as SensorAction,
      ),
    ),
  );

  const handleClick = () => {
    const empty = emptyDrone(target.site, organization);
    const emptyDroneWithDefaultValues = {
      ...empty,
      strategicDrone: {
        ...empty.strategicDrone,
        name: target.displayId,
      },
      holodroneNumber: target.collaborativeData?.infodroneId ?? null,
      aeroscopeNumber: target.collaborativeData?.aeroscopeId ?? null,
      model: target.collaborativeData?.model ?? null,
    } as PartialDrone;
    const validDrone =
      drone?.strategicDrone === null ? { ...drone, strategicDrone: emptyDroneWithDefaultValues.strategicDrone } : drone;
    dispatchUpdateSelectedDrone((validDrone as PartialDrone | null | undefined) ?? emptyDroneWithDefaultValues);
  };

  return (
    <VStack gap={0.25} width="100%" height="100%">
      {drone ? (
        <>
          <PopupContainer>
            <VStack gap="standardXS" width="100%">
              <HStack gap={3} width="100%">
                <InfoValue
                  label={formatMessage({ id: 'targetInfos.tab.drone.name' })}
                  value={drone.functionalName ?? formatMessage({ id: 'targetInfos.tab.drone.unknown' })}
                />
              </HStack>
              <HStack gap={3} width="100%">
                <InfoValue label={formatMessage({ id: 'targetInfos.tab.drone.model' })} value={drone.model} />
                <InfoValue
                  label={formatMessage({ id: 'targetInfos.tab.drone.manufacturer' })}
                  value={drone.manufacturer}
                />
              </HStack>
              <HStack gap={3} width="100%">
                <InfoValue
                  label={formatMessage({ id: 'targetInfos.tab.drone.serialNumber' })}
                  value={drone.serialNumber}
                />
                {/* TODO drone weight, range and autonomy */}
              </HStack>
            </VStack>
          </PopupContainer>
          <PopupContainer>
            <InfoValue
              label={formatMessage({ id: 'targetInfos.tab.drone.clearance' })}
              value={<ClearanceSelect key={target.clearance} target={target} isDisabled={!canManageTarget} />}
            />
          </PopupContainer>
        </>
      ) : (
        <Center width="100%" height="360px" backgroundColor="neutral.black">
          <VStack gap={2}>
            {isLoading ? (
              <Spinner size="lg" />
            ) : (
              <>
                <Icon as={EmptyPanelDroneIcon} boxSize={{ base: '80px', '4k': '120px' }} />
                <Text textStyle="screenStd" color="neutral.200" textAlign="center" userSelect="none">
                  <FormattedMessage id="targetInfos.tab.drone.emptyDrone" />
                </Text>
              </>
            )}
          </VStack>
        </Center>
      )}
      <DataSheetCardLink
        label={formatMessage({ id: `targetInfos.tab.drone.${target.droneCode ? 'edit' : 'create'}` })}
        icon={target.droneCode ? IcEdit : IcPlus}
        onClick={handleClick}
      />
    </VStack>
  );
}
